import {
  DatePicker,
  PolicyDispatchingPermission,
  Select,
  SelectOption,
  SelectSearch,
  assets,
  jsFileDownload
} from '@pulseops/common'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { StackScreenProps } from '@react-navigation/stack'
import { PolicyDispatchingStackParamList } from './PolicyDispatchingStackParamList'
import { ActivityIndicator, Text, View } from 'react-native'
import { pipe } from 'fp-ts/function'
import { ZIO } from '@mxt/zio'
import * as ExcelJS from 'exceljs'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import {
  borderedDatePickerContainerStyle,
  borderedSelectSearchStyle,
  borderedSelectStyle
} from './styles/policyDispatchingStyles'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import { usePolicyDispatchingOffice } from './components/PolicyDispatchingOfficeContext'
import {
  AdminReportItem,
  AgentListReportItem,
  ConfirmLettersReportItem,
  ContractsEmsCustomerReportItem,
  ContractsForSigningReportItem,
  ContractsInStockReportItem,
  DeductionReportItem,
  DeliveredContractsReportItem,
  IssuedReportItem,
  NonReturnLetterReportItem,
  NonReturnPostsReportItem,
  PolicyDispatchingService,
  ReceivedContractsReportItem,
  UndeliveredContractsByPostReportItem,
  UndeliveredContractsReportItem
} from './services/PolicyDispatchingService'
import { formatDate, getPermissions, getStatusLabel } from './utils/policyDispatchingUtils'
import { NotoSansRegularBase64 } from './assets/fonts/NotoSansRegularBase64'

const typeOptions = [
  { value: 'pdf', label: 'PDF' },
  { value: 'excel', label: 'EXCEL' }
]

const agentTypeOptions = [
  { value: 'BM', label: 'BM' },
  { value: 'UM', label: 'UM' },
  { value: 'PM', label: 'PM' },
  { value: 'SR', label: 'SR' },
  { value: 'PF', label: 'PF' },
  { value: 'AG', label: 'AG' },
  { value: 'All', label: 'All' }
]

const contactStatusLabels: Record<string, string> = {
  LOST_CONTACT: 'Mất liên lạc',
  NOT_CONTACTED: 'Chưa liên lạc',
  COMPLETED: 'Hoàn tất'
}

const getContactStatusLabel = (status: string | null) => (status ? contactStatusLabels[status] || status : '')

type ReportFormValues = {
  fromDate: Date | null
  toDate: Date | null
  type: SelectOption | null
  office: SelectOption | null
}

const DateTemplate = ({ control }: { control: any }) => {
  return (
    <View style={{ display: 'flex', flexDirection: 'column', gap: 16 } as any}>
      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="fromDate"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <DatePicker
                label={'Từ ngày'}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                maxDate={undefined}
                minDate={undefined}
                minDateMessage={''}
                errorMessage={error?.message}
                inputStyle={{ paddingBottom: 3 }}
                hideUnderline
                containerStyle={borderedDatePickerContainerStyle}
                required
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="toDate"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <DatePicker
                label={'Đến ngày'}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                maxDate={undefined}
                minDate={undefined}
                minDateMessage={''}
                errorMessage={error?.message}
                inputStyle={{ paddingBottom: 3 }}
                hideUnderline
                containerStyle={borderedDatePickerContainerStyle}
                required
              />
            )}
          />
        </View>
        <View style={styles.column_1_2} />
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="type"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Select
                label={'Xuất theo định dạng'}
                options={typeOptions}
                placeholder={'Chọn định dạng'}
                onChange={(e) => {
                  onChange(e)
                }}
                onBlur={onBlur}
                value={value}
                key={value?.value}
                errorMessage={error?.message}
                containerStyles={borderedSelectStyle}
                filled
                required
              />
            )}
          />
        </View>
      </View>
    </View>
  )
}

const FileTemplate = ({ control }: { control: any }) => {
  return (
    <View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="type"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Select
                label={'Xuất theo định dạng'}
                options={typeOptions}
                placeholder={'Chọn định dạng'}
                onChange={(e) => {
                  onChange(e)
                }}
                onBlur={onBlur}
                value={value}
                key={value?.value}
                errorMessage={error?.message}
                containerStyles={borderedSelectStyle}
                filled
                required
              />
            )}
          />
        </View>
      </View>
    </View>
  )
}

const AgentTemplate = ({ control, officesOptions = [] }: { control: any; officesOptions?: SelectOption[] }) => {
  return (
    <View style={{ display: 'flex', flexDirection: 'column', gap: 16 } as any}>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="agentType"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Select
                label={'Loại đại lý'}
                options={agentTypeOptions}
                placeholder={'Chọn loại đại lý'}
                onChange={(e) => {
                  onChange(e)
                }}
                onBlur={onBlur}
                value={value}
                key={value?.value}
                errorMessage={error?.message}
                containerStyles={borderedSelectStyle}
                filled
                required
              />
            )}
          />
        </View>
        <View style={styles.column_1_2} />
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="office"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <SelectSearch
                label={'Văn phòng'}
                options={officesOptions}
                placeholder={'Chọn văn phòng'}
                onChange={(e) => {
                  onChange(e)
                }}
                onBlur={onBlur}
                value={value}
                key={value?.value}
                errorMessage={error?.message}
                inputStyle={borderedSelectSearchStyle}
                disableUnderline
                required
              />
            )}
          />
        </View>
        <View style={styles.column_1_2} />
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="type"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Select
                label={'Xuất theo định dạng'}
                options={typeOptions}
                placeholder={'Chọn định dạng'}
                onChange={(e) => {
                  onChange(e)
                }}
                onBlur={onBlur}
                value={value}
                key={value?.value}
                errorMessage={error?.message}
                containerStyles={borderedSelectStyle}
                filled
                required
              />
            )}
          />
        </View>
        <View style={styles.column_1_2} />
      </View>
    </View>
  )
}

type FormType = 'date' | 'file' | 'agent'

const FORM_COMPONENTS: Record<FormType, (props: { control: any; officesOptions?: SelectOption[] }) => JSX.Element> = {
  date: DateTemplate,
  file: FileTemplate,
  agent: AgentTemplate
}

type ReportPage<T> = {
  items: T[]
  page: {
    first: boolean
    last: boolean
    page: number
    size: number
    totalElements: number
    totalPages: number
  }
  /** Some report endpoints return extra fields alongside items/page, e.g. printDate, totalContracts. */
  [key: string]: unknown
}

type ReportContext = {
  fromDateStr: string
  toDateStr: string
  officeLabel: string
  currentDateStr: string
  /** Extra response fields beyond `items`/`page`, e.g. `printDate`, `totalContracts` returned by some report endpoints. */
  meta: Record<string, unknown>
}

type ReportColumn<T> = {
  header: string
  get: (item: T, index: number, ctx: ReportContext) => string
}

type ReportSubHeaderField =
  | 'fromDate'
  | 'toDate'
  | 'office'
  | 'totalCount'
  | 'asOfDate'
  | ((ctx: ReportContext, itemCount: number) => string)

type ReportDefinition<T = any> = {
  id: number
  name: string
  title: string
  formType: FormType
  /** Set to false for reports whose API doesn't take a from/to date range (e.g. "as of today" snapshots). Defaults to true. */
  requiresDateRange?: boolean
  /** Set to true for reports that filter by the form's own "office" field instead of the global top-bar office. */
  usesLocalOfficeField?: boolean
  /** Set to false for reports whose API doesn't take an officeCode at all. Defaults to true. */
  requiresOffice?: boolean
  docTitle?: string | ((ctx: ReportContext) => string)
  fileNamePrefix?: string
  subHeaderRows?: ReportSubHeaderField[][]
  columns?: ReportColumn<T>[]
  search?: (payload: any) => ReturnType<typeof PolicyDispatchingService.searchConfirmLettersReport>
}

const REPORT_DEFINITIONS: ReportDefinition[] = [
  {
    id: 1,
    name: 'updatedLetter',
    title: 'Danh sách thư xác nhận đã cập nhật',
    formType: 'date',
    docTitle: 'BÁO CÁO THƯ XÁC NHẬN',
    fileNamePrefix: 'Bao_cao_thu_xac_nhan',
    subHeaderRows: [['fromDate', 'toDate'], ['totalCount'], ['office']],
    columns: [
      { header: 'STT', get: (_: ConfirmLettersReportItem, index: number) => String(index + 1) },
      { header: 'Số Hợp Đồng', get: (item: ConfirmLettersReportItem) => item.contractNumber || '' },
      { header: 'Mã ĐL', get: (item: ConfirmLettersReportItem) => item.agentNumber || '' },
      { header: 'Ngày phát hành', get: (item: ConfirmLettersReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày KH ký nhận HĐ', get: (item: ConfirmLettersReportItem) => item.customerSignedDate || '' },
      { header: 'Loại thư', get: (item: ConfirmLettersReportItem) => item.letterType || '' },
      { header: 'Ngày BO nhập thư', get: (item: ConfirmLettersReportItem) => item.letterEntryDate || '' }
    ],
    search: PolicyDispatchingService.searchConfirmLettersReport
  },
  {
    id: 2,
    name: 'agentLeader',
    title: 'Danh sách đại lý/trưởng nhóm bị trừ thu nhập',
    formType: 'date',
    docTitle: 'DANH SÁCH ĐẠI LÝ / TRƯỞNG NHÓM BỊ TRỪ THU NHẬP',
    fileNamePrefix: 'Danh_sach_dai_ly_truong_nhom_bi_tru_thu_nhap',
    subHeaderRows: [['fromDate', 'toDate', 'office']],
    columns: [
      { header: 'STT', get: (_: DeductionReportItem, index: number) => String(index + 1) },
      { header: 'VP giữ HĐ', get: (item: DeductionReportItem) => item.officeCode || '' },
      { header: 'VP Đại lý', get: (item: DeductionReportItem) => item.branch || '' },
      { header: 'Trưởng nhóm', get: (item: DeductionReportItem) => item.leaderCode || '' },
      { header: 'Mã ĐL', get: (item: DeductionReportItem) => item.agentNumber || '' },
      { header: 'Tên ĐL', get: (item: DeductionReportItem) => item.agentName || '' },
      { header: 'Cấp bậc ĐL', get: (item: DeductionReportItem) => item.agentType || '' },
      { header: 'Số Hợp đồng', get: (item: DeductionReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành', get: (item: DeductionReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày BO nhận', get: (item: DeductionReportItem) => item.stockInDate || '' },
      { header: 'Ngày gửi bưu điện', get: (item: DeductionReportItem) => item.deliveryDate || '' },
      {
        header: 'Số ngày trễ nhận',
        get: (item: DeductionReportItem) =>
          item.delayedReceiptDays !== null && item.delayedReceiptDays !== undefined
            ? String(item.delayedReceiptDays)
            : ''
      },
      { header: 'Ngày KH ký nhận', get: (item: DeductionReportItem) => item.customerSignedDate || '' },
      { header: 'Mã số bưu điện', get: (item: DeductionReportItem) => item.invoiceCode || '' }
    ],
    search: PolicyDispatchingService.searchDeductionReport
  },
  {
    id: 3,
    name: 'deliveredToAgent',
    title: 'Danh sách HĐ đã giao cho đại lý/nhóm',
    formType: 'date',
    docTitle: 'DANH SÁCH HỢP ĐỒNG ĐÃ GIAO CHO ĐẠI LÝ',
    fileNamePrefix: 'Danh_sach_hop_dong_da_giao_cho_dai_ly',
    subHeaderRows: [['fromDate', 'toDate', 'office']],
    columns: [
      { header: 'STT', get: (_: DeliveredContractsReportItem, index: number) => String(index + 1) },
      { header: 'VP', get: (item: DeliveredContractsReportItem) => item.branch || '' },
      { header: 'KV', get: (item: DeliveredContractsReportItem) => item.area || '' },
      { header: 'Vùng', get: (item: DeliveredContractsReportItem) => item.saleZone || '' },
      { header: 'Ban', get: (item: DeliveredContractsReportItem) => item.agentSalesBranch || '' },
      { header: 'Nhóm', get: (item: DeliveredContractsReportItem) => item.agentSalesUnitCode || '' },
      { header: 'MSĐL', get: (item: DeliveredContractsReportItem) => item.agentNumber || '' },
      { header: 'Tên ĐL', get: (item: DeliveredContractsReportItem) => item.agentName || '' },
      { header: 'Số Hợp Đồng', get: (item: DeliveredContractsReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành', get: (item: DeliveredContractsReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày BO nhận', get: (item: DeliveredContractsReportItem) => item.stockInDate || '' },
      { header: 'Ngày giao hợp đồng', get: (item: DeliveredContractsReportItem) => item.deliveryDate || '' },
      { header: 'Người nhận', get: (item: DeliveredContractsReportItem) => item.receiver || '' },
      { header: 'Lưu ý', get: (item: DeliveredContractsReportItem) => item.remark || '' }
    ],
    search: PolicyDispatchingService.searchDeliveredContractsReport
  },
  {
    id: 4,
    name: 'releasedContract',
    title: 'Danh sách HĐ đã phát hành treo bản tin',
    formType: 'file',
    requiresDateRange: false,
    docTitle: (ctx: ReportContext) =>
      `Danh sách hợp đồng đã giao về BO/GA ${ctx.officeLabel} tính đến ngày ${ctx.currentDateStr}`,
    fileNamePrefix: 'Danh_sach_hop_dong_da_giao_ve_BO_GA',
    subHeaderRows: [],
    columns: [
      { header: 'STT', get: (_: IssuedReportItem, index: number) => String(index + 1) },
      { header: 'VP giữ HĐ', get: (item: IssuedReportItem) => item.officeCode || '' },
      { header: 'VP', get: (item: IssuedReportItem) => item.branch || '' },
      { header: 'KV', get: (item: IssuedReportItem) => item.area || '' },
      { header: 'Vùng', get: (item: IssuedReportItem) => item.saleZone || '' },
      { header: 'Ban', get: (item: IssuedReportItem) => item.agentSalesBranch || '' },
      { header: 'Nhóm', get: (item: IssuedReportItem) => item.agentSalesUnitCode || '' },
      { header: 'MSĐL', get: (item: IssuedReportItem) => item.agentNumber || '' },
      { header: 'Tên ĐL', get: (item: IssuedReportItem) => item.agentName || '' },
      { header: 'Số HĐ', get: (item: IssuedReportItem) => item.contractNumber || '' },
      { header: 'Số HSYCBH', get: (item: IssuedReportItem) => item.proposalNumber || '' },
      { header: 'Ngày PH', get: (item: IssuedReportItem) => item.policyIssueDate || '' },
      {
        header: 'Số ngày chờ giao KH',
        get: (item: IssuedReportItem) =>
          item.waitingDays !== null && item.waitingDays !== undefined ? String(item.waitingDays) : ''
      }
    ],
    search: PolicyDispatchingService.searchIssuedReport
  },
  {
    id: 5,
    name: 'notDeliveredContract',
    title: 'Danh sách HĐ chưa bàn giao thư xác nhận quá ngày',
    formType: 'file',
    requiresDateRange: false,
    docTitle: 'DANH SÁCH HỢP ĐỒNG QUÁ 11 NGÀY CHƯA NỘP THƯ BÀN GIAO HỢP ĐỒNG VỀ BO',
    fileNamePrefix: 'Danh_sach_hop_dong_qua_han_chua_nop_thu_ban_giao',
    subHeaderRows: [['asOfDate', 'office']],
    columns: [
      { header: 'STT', get: (_: NonReturnLetterReportItem, index: number) => String(index + 1) },
      { header: 'VP', get: (item: NonReturnLetterReportItem) => item.branch || '' },
      { header: 'KV', get: (item: NonReturnLetterReportItem) => item.area || '' },
      { header: 'Vùng', get: (item: NonReturnLetterReportItem) => item.saleZone || '' },
      { header: 'Ban', get: (item: NonReturnLetterReportItem) => item.agentSalesBranch || '' },
      { header: 'Nhóm', get: (item: NonReturnLetterReportItem) => item.agentSalesUnitCode || '' },
      { header: 'MSĐL', get: (item: NonReturnLetterReportItem) => item.agentNumber || '' },
      { header: 'Tên ĐL', get: (item: NonReturnLetterReportItem) => item.agentName || '' },
      { header: 'Số Hợp Đồng', get: (item: NonReturnLetterReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành', get: (item: NonReturnLetterReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày BO nhận', get: (item: NonReturnLetterReportItem) => item.stockInDate || '' },
      { header: 'Ngày ĐL nhận HĐ', get: (item: NonReturnLetterReportItem) => item.deliveryDate || '' },
      {
        header: 'Số ngày trễ hạn',
        get: (item: NonReturnLetterReportItem) =>
          item.overdueDays !== null && item.overdueDays !== undefined ? String(item.overdueDays) : ''
      }
    ],
    search: PolicyDispatchingService.searchNonReturnLetterReport
  },
  {
    id: 6,
    name: 'contractSendByPost',
    title: 'Danh sách HĐ giao qua Bưu điện',
    formType: 'date',
    docTitle: 'DANH SÁCH HỢP ĐỒNG GIAO QUA BƯU ĐIỆN',
    fileNamePrefix: 'Danh_sach_hop_dong_giao_qua_buu_dien',
    subHeaderRows: [['fromDate', 'toDate', 'office'], ['totalCount']],
    columns: [
      { header: 'STT', get: (_: ContractsEmsCustomerReportItem, index: number) => String(index + 1) },
      {
        header: 'VP giữ HĐ',
        get: (_: ContractsEmsCustomerReportItem, __: number, ctx: ReportContext) => ctx.officeLabel
      },
      { header: 'VP Đại lý', get: (item: ContractsEmsCustomerReportItem) => item.branch || '' },
      { header: 'Trưởng nhóm', get: (item: ContractsEmsCustomerReportItem) => item.leaderCode || '' },
      { header: 'Mã ĐL', get: (item: ContractsEmsCustomerReportItem) => item.agentNumber || '' },
      { header: 'Tên ĐL', get: (item: ContractsEmsCustomerReportItem) => item.agentName || '' },
      { header: 'Cấp bậc ĐL', get: (item: ContractsEmsCustomerReportItem) => item.agentType || '' },
      { header: 'Số Hợp đồng', get: (item: ContractsEmsCustomerReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành', get: (item: ContractsEmsCustomerReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày BO nhận', get: (item: ContractsEmsCustomerReportItem) => item.stockInDate || '' },
      { header: 'Ngày gửi bưu điện', get: (item: ContractsEmsCustomerReportItem) => item.deliveryDate || '' },
      {
        header: 'Số ngày trễ nhận',
        get: (item: ContractsEmsCustomerReportItem) =>
          item.delayedReceiptDays !== null && item.delayedReceiptDays !== undefined
            ? String(item.delayedReceiptDays)
            : ''
      },
      { header: 'Ngày KH ký nhận', get: (item: ContractsEmsCustomerReportItem) => item.customerSignedDate || '' },
      { header: 'Người nhận', get: (item: ContractsEmsCustomerReportItem) => item.receiver || '' },
      { header: 'Mã số bưu điện', get: (item: ContractsEmsCustomerReportItem) => item.invoiceCode || '' }
    ],
    search: PolicyDispatchingService.searchContractsEmsCustomerReport
  },
  {
    id: 7,
    name: 'contractReceipt',
    title: 'Danh sách ký nhận HĐ',
    formType: 'file',
    requiresDateRange: false,
    docTitle: 'DANH SÁCH KÝ NHẬN HỢP ĐỒNG',
    fileNamePrefix: 'Danh_sach_ky_nhan_hop_dong',
    subHeaderRows: [
      [(ctx: ReportContext) => `Văn phòng đại lý: ${ctx.officeLabel}`],
      [(ctx: ReportContext) => `Tổng số hợp đồng trong DS: ${ctx.meta.totalContracts ?? ''}`],
      [(ctx: ReportContext) => `Tổng số hợp đồng nhận trong ngày: ${ctx.meta.totalReceivedToday ?? ''}`],
      [(ctx: ReportContext) => `Ngày in: ${ctx.meta.printDate ?? ctx.currentDateStr}`]
    ],
    columns: [
      { header: 'Số HĐ', get: (item: ContractsForSigningReportItem) => item.contractNumber || '' },
      { header: 'Ngày PH', get: (item: ContractsForSigningReportItem) => item.policyIssueDate || '' },
      { header: 'Tên khách hàng', get: (item: ContractsForSigningReportItem) => item.customerNumber || '' },
      { header: 'Tên Đại Lý', get: (item: ContractsForSigningReportItem) => item.agentName || '' },
      { header: 'MSĐL1', get: (item: ContractsForSigningReportItem) => item.agentNumber || '' },
      { header: 'MSĐL2', get: (item: ContractsForSigningReportItem) => item.sharedAgentNumber || '' },
      { header: 'Mã nhóm', get: (item: ContractsForSigningReportItem) => item.unitCode || '' },
      { header: 'Mã SP', get: (item: ContractsForSigningReportItem) => item.productCode || '' },
      { header: 'Ký nhận', get: () => '' }
    ],
    search: PolicyDispatchingService.searchContractsForSigningReport
  },
  {
    id: 8,
    name: 'inventoryContract',
    title: 'Danh sách HĐ tồn kho',
    formType: 'file',
    requiresDateRange: false,
    docTitle: 'DANH SÁCH HỢP ĐỒNG TỒN KHO',
    fileNamePrefix: 'Danh_sach_hop_dong_ton_kho',
    subHeaderRows: [
      ['office'],
      [(ctx: ReportContext) => `Ngày xuất báo cáo: ${ctx.currentDateStr}`],
      [() => `Thời gian xuất báo cáo: ${formatTime(new Date())}`],
      [(_: ReportContext, itemCount: number) => `Tổng số dữ liệu: ${itemCount}`]
    ],
    columns: [
      { header: 'STT', get: (_: ContractsInStockReportItem, index: number) => String(index + 1) },
      { header: 'Mã đại lý', get: (item: ContractsInStockReportItem) => item.agentNumber || '' },
      { header: 'Tên đại lý', get: (item: ContractsInStockReportItem) => item.agentName || '' },
      { header: 'Số hợp đồng', get: (item: ContractsInStockReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành gần nhất', get: (item: ContractsInStockReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày BO nhận', get: (item: ContractsInStockReportItem) => item.stockInDate || '' },
      {
        header: 'Số ngày trễ so với ngày BO nhận',
        get: (item: ContractsInStockReportItem) =>
          item.overdueDaysFromStockInDate !== null && item.overdueDaysFromStockInDate !== undefined
            ? String(item.overdueDaysFromStockInDate)
            : ''
      },
      {
        header: 'Số ngày trễ tính từ ngày phát hành',
        get: (item: ContractsInStockReportItem) =>
          item.overdueDaysFromPolicyIssueDate !== null && item.overdueDaysFromPolicyIssueDate !== undefined
            ? String(item.overdueDaysFromPolicyIssueDate)
            : ''
      },
      { header: 'Nhóm', get: (item: ContractsInStockReportItem) => item.agentSalesUnitCode || '' },
      { header: 'Ban', get: (item: ContractsInStockReportItem) => item.agentSalesBranch || '' },
      { header: 'Khu vực', get: (item: ContractsInStockReportItem) => item.area || '' },
      { header: 'VP', get: (item: ContractsInStockReportItem) => item.branch || '' },
      { header: 'Tình trạng', get: (item: ContractsInStockReportItem) => getStatusLabel(item.status || '') }
    ],
    search: PolicyDispatchingService.searchContractsInStockReport
  },
  {
    id: 9,
    name: 'contractClient',
    title: 'Danh sách HĐ nhận từ NB',
    formType: 'date',
    docTitle: 'DANH SÁCH HỢP ĐỒNG NHẬN TỪ NB',
    fileNamePrefix: 'Danh_sach_hop_dong_nhan_tu_NB',
    subHeaderRows: [['fromDate', 'toDate', 'office']],
    columns: [
      { header: 'STT', get: (_: ReceivedContractsReportItem, index: number) => String(index + 1) },
      { header: 'VP', get: (item: ReceivedContractsReportItem) => item.branch || '' },
      { header: 'KV', get: (item: ReceivedContractsReportItem) => item.area || '' },
      { header: 'Vùng', get: (item: ReceivedContractsReportItem) => item.saleZone || '' },
      { header: 'Ban', get: (item: ReceivedContractsReportItem) => item.agentSalesBranch || '' },
      { header: 'Nhóm', get: (item: ReceivedContractsReportItem) => item.agentSalesUnitCode || '' },
      { header: 'MSĐL', get: (item: ReceivedContractsReportItem) => item.agentNumber || '' },
      { header: 'Tên ĐL', get: (item: ReceivedContractsReportItem) => item.agentName || '' },
      { header: 'Số Hợp Đồng', get: (item: ReceivedContractsReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành', get: (item: ReceivedContractsReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày BO nhận', get: (item: ReceivedContractsReportItem) => item.stockInDate || '' },
      { header: 'Lưu ý', get: () => '' }
    ],
    search: PolicyDispatchingService.searchReceivedContractsReport
  },
  { id: 10, name: 'summaryReport', title: 'Báo cáo tổng hợp', formType: 'date' },
  {
    id: 11,
    name: 'latedContract',
    title: 'Danh sách HĐ phát hành trễ ngày ĐL chưa đến nhận',
    formType: 'file',
    requiresDateRange: false,
    docTitle: 'DANH SÁCH HỢP ĐỒNG PHÁT HÀNH QUÁ 5 NGÀY ĐẠI LÝ CHƯA ĐẾN NHẬN',
    fileNamePrefix: 'Danh_sach_hop_dong_phat_hanh_qua_han_chua_den_nhan',
    subHeaderRows: [['office']],
    columns: [
      { header: 'STT', get: (_: UndeliveredContractsReportItem, index: number) => String(index + 1) },
      { header: 'Mã ĐL', get: (item: UndeliveredContractsReportItem) => item.agentNumber || '' },
      { header: 'Tên ĐL', get: (item: UndeliveredContractsReportItem) => item.agentName || '' },
      { header: 'KV', get: (item: UndeliveredContractsReportItem) => item.area || '' },
      { header: 'Vùng', get: (item: UndeliveredContractsReportItem) => item.saleZone || '' },
      { header: 'Ban', get: (item: UndeliveredContractsReportItem) => item.agentSalesBranch || '' },
      { header: 'Nhóm', get: (item: UndeliveredContractsReportItem) => item.agentSalesUnitCode || '' },
      { header: 'Số HĐ', get: (item: UndeliveredContractsReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành', get: (item: UndeliveredContractsReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày nhập kho', get: (item: UndeliveredContractsReportItem) => item.stockInDate || '' },
      {
        header: 'Số ngày quá hạn',
        get: (item: UndeliveredContractsReportItem) =>
          item.overdueDays !== null && item.overdueDays !== undefined ? String(item.overdueDays) : ''
      },
      { header: 'Quầy BO/GA giữ HĐ', get: (item: UndeliveredContractsReportItem) => item.officeCode || '' },
      { header: 'ĐL trực thuộc Văn Phòng', get: (item: UndeliveredContractsReportItem) => item.branch || '' },
      { header: 'Tình trạng', get: (item: UndeliveredContractsReportItem) => getStatusLabel(item.status || '') }
    ],
    search: PolicyDispatchingService.searchUndeliveredContractsReport
  },
  {
    id: 12,
    name: 'pendingContract',
    title: 'Danh sách HĐ gửi Bưu điện chưa trả giấy báo phát',
    formType: 'date',
    docTitle: 'DANH SÁCH HỢP ĐỒNG GỬI BƯU ĐIỆN CHƯA TRẢ GIẤY BÁO PHÁT',
    fileNamePrefix: 'Danh_sach_hop_dong_gui_buu_dien_chua_tra_giay_bao_phat',
    subHeaderRows: [
      ['fromDate', 'toDate', 'office'],
      [(_: ReportContext, itemCount: number) => `Tổng số hợp đồng: ${itemCount}`]
    ],
    columns: [
      { header: 'STT', get: (_: NonReturnPostsReportItem, index: number) => String(index + 1) },
      { header: 'VP giữ HĐ', get: (item: NonReturnPostsReportItem) => item.officeCode || '' },
      { header: 'Trưởng nhóm', get: (item: NonReturnPostsReportItem) => item.leaderCode || '' },
      { header: 'Mã ĐL', get: (item: NonReturnPostsReportItem) => item.agentNumber || '' },
      { header: 'Tên ĐL', get: (item: NonReturnPostsReportItem) => item.agentName || '' },
      { header: 'Cấp bậc ĐL', get: (item: NonReturnPostsReportItem) => item.agentType || '' },
      { header: 'Số Hợp đồng', get: (item: NonReturnPostsReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành', get: (item: NonReturnPostsReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày BO nhận', get: (item: NonReturnPostsReportItem) => item.stockInDate || '' },
      { header: 'Ngày gửi bưu điện', get: (item: NonReturnPostsReportItem) => item.deliveryDate || '' },
      {
        header: 'Số ngày trễ nhận',
        get: (item: NonReturnPostsReportItem) =>
          item.delayedReceiptDays !== null && item.delayedReceiptDays !== undefined
            ? String(item.delayedReceiptDays)
            : ''
      },
      {
        header: 'Số ngày gửi bưu điện',
        get: (item: NonReturnPostsReportItem) =>
          item.postalDeliveryDays !== null && item.postalDeliveryDays !== undefined
            ? String(item.postalDeliveryDays)
            : ''
      },
      { header: 'Mã số bưu điện', get: (item: NonReturnPostsReportItem) => item.invoiceCode || '' }
    ],
    search: PolicyDispatchingService.searchNonReturnPostsReport
  },
  {
    id: 13,
    name: 'returnContract',
    title: 'Danh sách HĐ gửi EMS bị Bưu điện trả lại',
    formType: 'date',
    docTitle: 'DANH SÁCH HỢP ĐỒNG GỬI EMS BỊ BƯU ĐIỆN TRẢ LẠI',
    fileNamePrefix: 'Danh_sach_hop_dong_gui_EMS_bi_buu_dien_tra_lai',
    subHeaderRows: [['fromDate', 'toDate', 'office']],
    columns: [
      { header: 'STT', get: (_: UndeliveredContractsByPostReportItem, index: number) => String(index + 1) },
      { header: 'VP giữ HĐ', get: (item: UndeliveredContractsByPostReportItem) => item.officeCode || '' },
      { header: 'VP Đại lý', get: (item: UndeliveredContractsByPostReportItem) => item.branch || '' },
      { header: 'Trưởng nhóm', get: (item: UndeliveredContractsByPostReportItem) => item.leaderCode || '' },
      { header: 'Mã ĐL', get: (item: UndeliveredContractsByPostReportItem) => item.agentNumber || '' },
      { header: 'Cấp bậc ĐL', get: (item: UndeliveredContractsByPostReportItem) => item.agentType || '' },
      { header: 'Số Hợp đồng', get: (item: UndeliveredContractsByPostReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành', get: (item: UndeliveredContractsByPostReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày BO nhận', get: (item: UndeliveredContractsByPostReportItem) => item.stockInDate || '' },
      { header: 'Ngày gửi bưu điện', get: (item: UndeliveredContractsByPostReportItem) => item.deliveryDate || '' },
      {
        header: 'Số ngày trễ nhận',
        get: (item: UndeliveredContractsByPostReportItem) =>
          item.delayedReceiptDays !== null && item.delayedReceiptDays !== undefined
            ? String(item.delayedReceiptDays)
            : ''
      },
      { header: 'Mã số bưu điện', get: (item: UndeliveredContractsByPostReportItem) => item.invoiceCode || '' },
      {
        header: 'Ngày bưu điện trả về',
        get: (item: UndeliveredContractsByPostReportItem) => item.postOfficeReturnDate || ''
      },
      { header: 'Lý do trả về', get: (item: UndeliveredContractsByPostReportItem) => item.remark || '' },
      {
        header: 'Tình trạng liên hệ',
        get: (item: UndeliveredContractsByPostReportItem) => getContactStatusLabel(item.contactStatus)
      },
      {
        header: 'Số lần gửi lại',
        get: (item: UndeliveredContractsByPostReportItem) =>
          item.resendCount !== null && item.resendCount !== undefined ? String(item.resendCount) : ''
      }
    ],
    search: PolicyDispatchingService.searchUndeliveredContractsByPostReport
  },
  {
    id: 14,
    name: 'AGContract',
    title: 'Danh sách AG/BM/UM/PM',
    formType: 'agent',
    requiresDateRange: false,
    usesLocalOfficeField: true,
    docTitle: 'DANH SÁCH AG/BM/UM/PM',
    fileNamePrefix: 'Danh_sach_AG_BM_UM_PM',
    subHeaderRows: [[(ctx: ReportContext) => `Loại đại lý: ${ctx.meta.agentTypeLabel ?? ''}`], ['office']],
    columns: [
      { header: 'STT', get: (_: AgentListReportItem, index: number) => String(index + 1) },
      { header: 'Mã ĐL', get: (item: AgentListReportItem) => item.agentNumber || '' },
      { header: 'Tên ĐL', get: (item: AgentListReportItem) => item.agentName || '' },
      { header: 'Văn phòng', get: (item: AgentListReportItem) => item.officeCode || '' },
      { header: 'Nhóm', get: (item: AgentListReportItem) => item.agentSalesUnitCode || '' },
      { header: 'Ban', get: (item: AgentListReportItem) => item.agentSalesBranch || '' },
      { header: 'Munit', get: (item: AgentListReportItem) => item.mumUnitCode || '' },
      { header: 'Mã số BM', get: (item: AgentListReportItem) => item.leaderCode || '' }
    ],
    search: PolicyDispatchingService.searchAgentListReport
  },
  {
    id: 15,
    name: 'customerSignature',
    title: 'Báo cáo tổng hợp cho Admin',
    formType: 'date',
    requiresOffice: false,
    docTitle: 'BÁO CÁO TỔNG HỢP CHO ADMIN',
    fileNamePrefix: 'Bao_cao_tong_hop_cho_admin',
    subHeaderRows: [['fromDate', 'toDate']],
    columns: [
      { header: 'STT', get: (_: AdminReportItem, index: number) => String(index + 1) },
      { header: 'Số hợp đồng', get: (item: AdminReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành', get: (item: AdminReportItem) => item.policyIssueDate || '' },
      { header: 'MSĐL', get: (item: AdminReportItem) => item.agentNumber || '' },
      { header: 'Tên sản phẩm chính', get: (item: AdminReportItem) => item.productName || '' },
      { header: 'Mã VP', get: (item: AdminReportItem) => item.branchCode || '' },
      { header: 'Ngày nhập kho', get: (item: AdminReportItem) => item.stockInDate || '' },
      { header: 'Ngày giao', get: (item: AdminReportItem) => item.deliveryDate || '' },
      { header: 'Ngày nhập thư', get: (item: AdminReportItem) => item.letterEntryDate || '' },
      { header: 'Ngày KH ký thư', get: (item: AdminReportItem) => item.customerSignedDate || '' },
      { header: 'Văn phòng nhập thư', get: (item: AdminReportItem) => item.officeCode || '' },
      { header: 'Ngày LAS', get: (item: AdminReportItem) => item.lasAckRecordedDate || '' },
      { header: 'ACK đã lưu trên FileNet', get: (item: AdminReportItem) => (item.ackFileNet ? 'Có' : 'Không') }
    ],
    search: PolicyDispatchingService.searchAdminReport
  },
  {
    id: 16,
    name: 'inventoryContractAdmin',
    title: 'Danh sách HĐ tồn kho Admin',
    formType: 'file',
    requiresOffice: false,
    requiresDateRange: false,
    docTitle: 'DANH SÁCH HỢP ĐỒNG TỒN KHO',
    fileNamePrefix: 'Danh_sach_hop_dong_ton_kho_Admin',
    subHeaderRows: [
      [(ctx: ReportContext) => `Ngày xuất báo cáo: ${ctx.currentDateStr}`],
      [() => `Thời gian xuất báo cáo: ${formatTime(new Date())}`],
      [(_: ReportContext, itemCount: number) => `Tổng số dữ liệu: ${itemCount}`]
    ],
    columns: [
      { header: 'STT', get: (_: ContractsInStockReportItem, index: number) => String(index + 1) },
      { header: 'Mã đại lý', get: (item: ContractsInStockReportItem) => item.agentNumber || '' },
      { header: 'Tên đại lý', get: (item: ContractsInStockReportItem) => item.agentName || '' },
      { header: 'Số hợp đồng', get: (item: ContractsInStockReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành gần nhất', get: (item: ContractsInStockReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày BO nhận', get: (item: ContractsInStockReportItem) => item.stockInDate || '' },
      {
        header: 'Số ngày trễ so với ngày BO nhận',
        get: (item: ContractsInStockReportItem) =>
          item.overdueDaysFromStockInDate !== null && item.overdueDaysFromStockInDate !== undefined
            ? String(item.overdueDaysFromStockInDate)
            : ''
      },
      {
        header: 'Số ngày trễ tính từ ngày phát hành',
        get: (item: ContractsInStockReportItem) =>
          item.overdueDaysFromPolicyIssueDate !== null && item.overdueDaysFromPolicyIssueDate !== undefined
            ? String(item.overdueDaysFromPolicyIssueDate)
            : ''
      },
      { header: 'Nhóm', get: (item: ContractsInStockReportItem) => item.agentSalesUnitCode || '' },
      { header: 'Ban', get: (item: ContractsInStockReportItem) => item.agentSalesBranch || '' },
      { header: 'Khu vực', get: (item: ContractsInStockReportItem) => item.area || '' },
      { header: 'VP', get: (item: ContractsInStockReportItem) => item.branch || '' },
      { header: 'Tình trạng', get: (item: ContractsInStockReportItem) => getStatusLabel(item.status || '') },
      { header: 'VP giữ HĐ', get: (item: ContractsInStockReportItem) => item.officeContract || '' }
    ],
    search: PolicyDispatchingService.searchContractsInStockReport
  },
  {
    id: 17,
    name: 'contractClientAdmin',
    title: 'Danh sách HĐ nhận từ NB Admin',
    formType: 'date',
    requiresOffice: false,
    docTitle: 'DANH SÁCH HỢP ĐỒNG NHẬN TỪ NB',
    fileNamePrefix: 'Danh_sach_hop_dong_nhan_tu_NB_Admin',
    subHeaderRows: [['fromDate', 'toDate']],
    columns: [
      { header: 'STT', get: (_: ReceivedContractsReportItem, index: number) => String(index + 1) },
      { header: 'VP', get: (item: ReceivedContractsReportItem) => item.branch || '' },
      { header: 'KV', get: (item: ReceivedContractsReportItem) => item.area || '' },
      { header: 'Vùng', get: (item: ReceivedContractsReportItem) => item.saleZone || '' },
      { header: 'Ban', get: (item: ReceivedContractsReportItem) => item.agentSalesBranch || '' },
      { header: 'Nhóm', get: (item: ReceivedContractsReportItem) => item.agentSalesUnitCode || '' },
      { header: 'MSĐL', get: (item: ReceivedContractsReportItem) => item.agentNumber || '' },
      { header: 'Tên ĐL', get: (item: ReceivedContractsReportItem) => item.agentName || '' },
      { header: 'Số Hợp Đồng', get: (item: ReceivedContractsReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành', get: (item: ReceivedContractsReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày BO nhận', get: (item: ReceivedContractsReportItem) => item.stockInDate || '' },
      { header: 'VP nhận HĐ', get: (item: ReceivedContractsReportItem) => item.officeContract || '' },
      { header: 'Lưu ý', get: () => '' }
    ],
    search: PolicyDispatchingService.searchReceivedContractsReport
  },
  {
    id: 18,
    name: 'updatedLetterAdmin',
    title: 'Danh sách thư xác nhận đã cập nhật Admin',
    formType: 'date',
    requiresOffice: false,
    docTitle: 'BÁO CÁO THƯ XÁC NHẬN',
    fileNamePrefix: 'Bao_cao_thu_xac_nhan_Admin',
    subHeaderRows: [['fromDate', 'toDate'], ['totalCount']],
    columns: [
      { header: 'STT', get: (_: ConfirmLettersReportItem, index: number) => String(index + 1) },
      { header: 'Số Hợp Đồng', get: (item: ConfirmLettersReportItem) => item.contractNumber || '' },
      { header: 'Mã ĐL', get: (item: ConfirmLettersReportItem) => item.agentNumber || '' },
      { header: 'Ngày phát hành', get: (item: ConfirmLettersReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày KH ký nhận HĐ', get: (item: ConfirmLettersReportItem) => item.customerSignedDate || '' },
      { header: 'Loại thư', get: (item: ConfirmLettersReportItem) => item.letterType || '' },
      { header: 'Ngày BO nhập thư', get: (item: ConfirmLettersReportItem) => item.letterEntryDate || '' },
      { header: 'VP nhận HĐ', get: (item: ConfirmLettersReportItem) => item.officeContract || '' }
    ],
    search: PolicyDispatchingService.searchConfirmLettersReport
  },
  {
    id: 19,
    name: 'deliveredToAgentAdmin',
    title: 'Danh sách HĐ đã giao cho đại lý/nhóm Admin',
    formType: 'date',
    requiresOffice: false,
    docTitle: 'DANH SÁCH HỢP ĐỒNG ĐÃ GIAO CHO ĐẠI LÝ',
    fileNamePrefix: 'Danh_sach_hop_dong_da_giao_cho_dai_ly_Admin',
    subHeaderRows: [['fromDate', 'toDate']],
    columns: [
      { header: 'STT', get: (_: DeliveredContractsReportItem, index: number) => String(index + 1) },
      { header: 'VP', get: (item: DeliveredContractsReportItem) => item.branch || '' },
      { header: 'KV', get: (item: DeliveredContractsReportItem) => item.area || '' },
      { header: 'Vùng', get: (item: DeliveredContractsReportItem) => item.saleZone || '' },
      { header: 'Ban', get: (item: DeliveredContractsReportItem) => item.agentSalesBranch || '' },
      { header: 'Nhóm', get: (item: DeliveredContractsReportItem) => item.agentSalesUnitCode || '' },
      { header: 'MSĐL', get: (item: DeliveredContractsReportItem) => item.agentNumber || '' },
      { header: 'Tên ĐL', get: (item: DeliveredContractsReportItem) => item.agentName || '' },
      { header: 'Số Hợp Đồng', get: (item: DeliveredContractsReportItem) => item.contractNumber || '' },
      { header: 'Ngày phát hành', get: (item: DeliveredContractsReportItem) => item.policyIssueDate || '' },
      { header: 'Ngày BO nhận', get: (item: DeliveredContractsReportItem) => item.stockInDate || '' },
      { header: 'Ngày giao hợp đồng', get: (item: DeliveredContractsReportItem) => item.deliveryDate || '' },
      { header: 'Người nhận', get: (item: DeliveredContractsReportItem) => item.receiver || '' },
      { header: 'VP nhận HĐ', get: (item: DeliveredContractsReportItem) => item.officeContract || '' },
      { header: 'Lưu ý', get: (item: DeliveredContractsReportItem) => item.remark || '' }
    ],
    search: PolicyDispatchingService.searchDeliveredContractsReport
  }
]

const formatTime = (date: Date) => {
  const hours = `${date.getHours()}`.padStart(2, '0')
  const minutes = `${date.getMinutes()}`.padStart(2, '0')
  const seconds = `${date.getSeconds()}`.padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

const registerVietnameseFont = (doc: jsPDF) => {
  doc.addFileToVFS('NotoSans-Regular.ttf', NotoSansRegularBase64)
  doc.addFont('NotoSans-Regular.ttf', 'NotoSans', 'normal')
  doc.setFont('NotoSans', 'normal')
}

const unwrapEither = (response: unknown): unknown =>
  response && typeof response === 'object' && '_tag' in response
    ? (response as any)._tag === 'Right'
      ? (response as any).right
      : null
    : response

const hasReportBody = (response: unknown): response is { body: ReportPage<any> } =>
  Boolean(response) && typeof response === 'object' && 'body' in (response as object)

const runReportSearch = async (search: ReportDefinition['search'], payload: Record<string, unknown>) => {
  if (!search) {
    return null
  }
  const response = await pipe(search(payload), ZIO.runPromise({}))
  const data = unwrapEither(response)
  return hasReportBody(data) ? (data.body as ReportPage<any>) : null
}

const fetchAllReportItems = async (search: ReportDefinition['search'], basePayload: Record<string, unknown>) => {
  const first = await runReportSearch(search, { ...basePayload, page: 0 })
  if (!first || first.items.length === 0) {
    return { items: [] as any[], meta: {} as Record<string, unknown> }
  }

  const remaining = await Promise.all(
    Array.from({ length: Math.max(first.page.totalPages - 1, 0) }, (_, index) =>
      runReportSearch(search, { ...basePayload, page: index + 1 })
    )
  )

  const items = [first, ...remaining].flatMap((page) => (page ? page.items : []))
  const { items: _items, page: _page, ...meta } = first
  return { items, meta }
}

const buildSubHeaderLine = (fields: ReportSubHeaderField[], ctx: ReportContext, itemCount: number) =>
  fields
    .map((field) => {
      if (typeof field === 'function') {
        return field(ctx, itemCount)
      }
      switch (field) {
        case 'fromDate':
          return `Từ ngày: ${ctx.fromDateStr}`
        case 'toDate':
          return `Đến ngày: ${ctx.toDateStr}`
        case 'office':
          return `Văn phòng: ${ctx.officeLabel}`
        case 'totalCount':
          return `Tổng số thư: ${itemCount}`
        case 'asOfDate':
          return `Tính đến ngày: ${ctx.currentDateStr}`
      }
    })
    .join('      ')

const resolveDocTitle = (def: ReportDefinition, ctx: ReportContext) =>
  typeof def.docTitle === 'function' ? def.docTitle(ctx) : def.docTitle || def.title

const buildFileSuffix = (ctx: ReportContext) =>
  ctx.fromDateStr && ctx.toDateStr
    ? `${ctx.fromDateStr.replace(/\//g, '-')}_${ctx.toDateStr.replace(/\//g, '-')}`
    : ctx.currentDateStr.replace(/\//g, '-')

const exportReportExcel = async (def: ReportDefinition, items: any[], ctx: ReportContext) => {
  const columns = def.columns || []
  const subHeaderRows = def.subHeaderRows || []
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet(def.name.slice(0, 31))

  ws.mergeCells(1, 1, 1, columns.length)
  const titleCell = ws.getCell(1, 1)
  titleCell.value = resolveDocTitle(def, ctx)
  titleCell.font = { bold: true, size: 14 }
  titleCell.alignment = { horizontal: 'center' }

  subHeaderRows.forEach((fields, rowIndex) => {
    const cell = ws.getCell(2 + rowIndex, 1)
    cell.value = buildSubHeaderLine(fields, ctx, items.length)
    cell.font = { bold: true }
  })

  const headerRowIndex = 2 + subHeaderRows.length + (subHeaderRows.length > 0 ? 1 : 0)
  columns.forEach((column, index) => {
    const cell = ws.getCell(headerRowIndex, index + 1)
    cell.value = column.header
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFED1B2E' } }
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
  })

  items.forEach((item, itemIndex) => {
    const rowIndex = headerRowIndex + 1 + itemIndex
    columns.forEach((column, columnIndex) => {
      ws.getCell(rowIndex, columnIndex + 1).value = column.get(item, itemIndex, ctx)
    })
  })

  ws.columns.forEach((column) => {
    let maxLength = 12
    column.eachCell?.({ includeEmpty: true }, (cell) => {
      const valueLength = cell.value ? String(cell.value).length : 0
      if (valueLength > maxLength) {
        maxLength = valueLength
      }
    })
    column.width = Math.min(maxLength + 2, 40)
  })

  const buffer = await wb.xlsx.writeBuffer({ useStyles: true })
  jsFileDownload(buffer, `${def.fileNamePrefix || def.name}_${buildFileSuffix(ctx)}.xlsx`)
}

const exportReportPdf = (def: ReportDefinition, items: any[], ctx: ReportContext) => {
  const columns = def.columns || []
  const subHeaderRows = def.subHeaderRows || []
  const doc = new jsPDF({ orientation: columns.length > 8 ? 'landscape' : 'portrait' })
  registerVietnameseFont(doc)

  const pageWidth = doc.internal.pageSize.getWidth()
  doc.setFontSize(14)
  doc.text(resolveDocTitle(def, ctx), pageWidth / 2, 15, { align: 'center' })

  doc.setFontSize(11)
  subHeaderRows.forEach((fields, rowIndex) => {
    doc.text(buildSubHeaderLine(fields, ctx, items.length), 14, 25 + rowIndex * 7)
  })

  const startY = subHeaderRows.length > 0 ? 25 + subHeaderRows.length * 7 + 6 : 22
  autoTable(doc, {
    startY,
    styles: { font: 'NotoSans', fontStyle: 'normal', fontSize: columns.length > 8 ? 8 : 10 },
    headStyles: { fillColor: [237, 27, 46], textColor: 255 },
    head: [columns.map((column) => column.header)],
    body: items.map((item, index) => columns.map((column) => column.get(item, index, ctx)))
  })

  doc.save(`${def.fileNamePrefix || def.name}_${buildFileSuffix(ctx)}.pdf`)
}

type Props = StackScreenProps<PolicyDispatchingStackParamList, 'PDReportScreen'>

export default function PDReportScreen({ route, navigation }: Props) {
  const { officeCode } = usePolicyDispatchingOffice()
  const { control, getValues } = useForm<ReportFormValues>({
    defaultValues: {
      fromDate: null,
      toDate: null,
      type: typeOptions[0],
      office: null
    }
  })
  const [isLoading, setIsLoading] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState({ title: '', message: '' })
  const [openModal, setOpenModal] = React.useState(false)
  const [officesOptions, setOfficesOptions] = React.useState<SelectOption[]>([])
  const { type } = route.params || {}
  const permissions = getPermissions()
  const allowDownload =
    permissions.includes(PolicyDispatchingPermission.ReportSearch) ||
    permissions.includes(PolicyDispatchingPermission.ReportSearchAdmin)

  React.useEffect(() => {
    pipe(
      PolicyDispatchingService.getOffices,
      ZIO.map((res) => {
        setOfficesOptions(res.body.items.map((office) => ({ value: office.officeCode, label: office.officeCode })))
      }),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          console.error('Unable to load offices', error)
        })
      ),
      ZIO.unsafeRun({})
    )
  }, [])

  const template = REPORT_DEFINITIONS.find((item) => item.name === type) || null
  const FormComponent = template ? FORM_COMPONENTS[template.formType] : null

  const handleDownload = async () => {
    if (!template || !template.search || !template.columns) {
      setModalInfo({ title: 'Tải về', message: 'Chức năng đang được phát triển.' })
      setOpenModal(true)
      return
    }

    const values = getValues()
    const requiresDateRange = template.requiresDateRange !== false
    if (requiresDateRange && (!values.fromDate || !values.toDate)) {
      setModalInfo({ title: 'Tải về', message: 'Vui lòng chọn từ ngày và đến ngày trước khi tải về.' })
      setOpenModal(true)
      return
    }
    if (!values.type) {
      setModalInfo({ title: 'Tải về', message: 'Vui lòng chọn định dạng xuất trước khi tải về.' })
      setOpenModal(true)
      return
    }

    const requiresOffice = template.requiresOffice !== false
    const resolvedOfficeCode = template.usesLocalOfficeField ? values.office?.value || null : officeCode
    if (requiresOffice && !resolvedOfficeCode) {
      setModalInfo({ title: 'Văn phòng', message: 'Vui lòng chọn văn phòng trước khi tải về.' })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    try {
      const fromDateStr = requiresDateRange ? formatDate(values.fromDate) || '' : ''
      const toDateStr = requiresDateRange ? formatDate(values.toDate) || '' : ''
      const searchPayload: Record<string, unknown> = requiresDateRange
        ? { fromDate: fromDateStr, toDate: toDateStr }
        : {}
      if (requiresOffice) {
        searchPayload.officeCode = resolvedOfficeCode
      }
      if (template.formType === 'agent') {
        searchPayload.agentType = (values as any).agentType?.value || null
      }
      const { items, meta } = await fetchAllReportItems(template.search, searchPayload)

      if (items.length === 0) {
        setModalInfo({ title: 'Tải về', message: 'Không có dữ liệu để tải về.' })
        setOpenModal(true)
        return
      }

      const ctx: ReportContext = {
        fromDateStr,
        toDateStr,
        officeLabel: resolvedOfficeCode || '',
        currentDateStr: formatDate(new Date()) || '',
        meta: { ...meta, agentTypeLabel: (values as any).agentType?.label ?? '' }
      }
      if (values.type.value === 'pdf') {
        exportReportPdf(template, items, ctx)
      } else {
        await exportReportExcel(template, items, ctx)
      }
    } catch (error) {
      console.error('Unable to download report', error)
      setModalInfo({ title: 'Tải về', message: 'Không thể tải dữ liệu. Vui lòng thử lại.' })
      setOpenModal(true)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <View style={{ ...styles.container, position: 'relative' } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}
      <Text style={styles.title}>Báo cáo</Text>
      <View style={styles.wrapper}>
        <Text style={styles.subTitle}>{template?.title}</Text>
        <Text style={styles.noted}>(* thông tin cần nhập)</Text>
        {FormComponent && <FormComponent control={control} officesOptions={officesOptions} />}
        <button
          style={{
            ...(isLoading || !allowDownload ? styles.disabledButton : styles.submitButton),
            width: 'fit-content',
            marginTop: 24
          }}
          type="button"
          onClick={handleDownload}
          disabled={isLoading || !allowDownload}
        >
          <span style={styles.mr_5}>
            <assets.PDDownload />
          </span>
          Tải về
        </button>
      </View>
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  loadingOverlay: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  wrapper: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  subTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
    paddingTop: 4,
    paddingRight: 12,
    paddingBottom: 4,
    paddingLeft: 12,
    borderRadius: 4,
    backgroundColor: '#e7000b',
    width: 'fit-content'
  },
  noted: {
    fontSize: '12px',
    color: '#6a7282',
    fontStyle: 'italic',
    display: 'flex',
    flexDirection: 'row',
    marginTop: 24,
    marginBottom: 24
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    width: '100%'
  },
  column_1_4: {
    width: 'calc(100% / 4 - 18px)'
  },
  column_1_2: {
    width: 'calc(100% / 2 - 12px)'
  },
  submitButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600'
  },
  disabledButton: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  mr_5: {
    marginRight: '5px'
  }
}
